import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    getKYCStatus,
    createKYCSession,
    refreshKYCSession,
    KYCStatus,
    KYCSession,
    KYCStatusValue,
    canRetryKYC,
    isKYCPending,
    getKYCStatusMessage
} from '../api/kyc';

// Re-export types and helpers
export type { KYCStatus, KYCSession, KYCStatusValue };
export { canRetryKYC, isKYCPending, getKYCStatusMessage };

interface KYCContextValue {
    // Status state
    status: KYCStatus | null;
    isLoading: boolean;
    error: string | null;
    isKYCRequired: boolean;
    refetch: () => Promise<void>;

    // Session state
    session: KYCSession | null;
    isCreating: boolean;
    sessionError: string | null;
    createSession: () => Promise<void>;
    refreshSession: () => Promise<string | null>;
    clearSession: () => void;
}

const KYCContext = createContext<KYCContextValue | undefined>(undefined);

interface KYCProviderProps {
    children: ReactNode;
}

export function KYCProvider({ children }: KYCProviderProps) {
    const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();

    // Status state
    const [status, setStatus] = useState<KYCStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Session state
    const [session, setSession] = useState<KYCSession | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [sessionError, setSessionError] = useState<string | null>(null);

    // Security: Fail-closed - KYC is required for authenticated users when:
    // 1. Still loading (don't show unprotected content during load)
    // 2. Status is null/undefined (API failed or not yet fetched)
    // 3. Status is not APPROVED
    // This prevents bypassing KYC by causing API errors or racing the load state
    const isKYCRequired = isAuthenticated && (isLoading || !status || status.status !== 'APPROVED');

    // Fetch KYC status
    const fetchStatus = useCallback(async () => {
        if (!isAuthenticated) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const kycStatus = await getKYCStatus(token);
            setStatus(kycStatus);
        } catch (err) {
            console.error('Failed to fetch KYC status:', err);
            setError('Failed to fetch KYC status');
            setStatus(null);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, getAccessTokenSilently]);

    // Fetch status on mount and when auth changes
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Create KYC session for Sumsub WebSDK
    const createSessionHandler = useCallback(async () => {
        if (!isAuthenticated || !user?.email) {
            setSessionError('User not authenticated');
            return;
        }

        setIsCreating(true);
        setSessionError(null);

        try {
            const token = await getAccessTokenSilently();
            const newSession = await createKYCSession(token, user.email);
            setSession(newSession);
        } catch (err: any) {
            console.error('Failed to create KYC session:', err);
            const errorMessage = err?.response?.data?.error?.message || 'Failed to create verification session';
            setSessionError(errorMessage);
            setSession(null);
        } finally {
            setIsCreating(false);
        }
    }, [isAuthenticated, user?.email, getAccessTokenSilently]);

    // Refresh KYC session token - returns the new access token or null on failure
    const refreshSessionHandler = useCallback(async (): Promise<string | null> => {
        if (!isAuthenticated) {
            setSessionError('User not authenticated');
            return null;
        }

        try {
            const token = await getAccessTokenSilently();
            const refreshedSession = await refreshKYCSession(token);
            setSession(refreshedSession);
            return refreshedSession.accessToken;
        } catch (err: any) {
            console.error('Failed to refresh KYC session:', err);
            const errorMessage = err?.response?.data?.error?.message || 'Failed to refresh session';
            setSessionError(errorMessage);
            return null;
        }
    }, [isAuthenticated, getAccessTokenSilently]);

    // Clear session
    const clearSession = useCallback(() => {
        setSession(null);
        setSessionError(null);
    }, []);

    const value: KYCContextValue = {
        // Status
        status,
        isLoading,
        error,
        isKYCRequired,
        refetch: fetchStatus,

        // Session
        session,
        isCreating,
        sessionError,
        createSession: createSessionHandler,
        refreshSession: refreshSessionHandler,
        clearSession
    };

    return <KYCContext.Provider value={value}>{children}</KYCContext.Provider>;
}

/**
 * Hook to access KYC status
 */
export function useKYCStatus() {
    const context = useContext(KYCContext);
    if (context === undefined) {
        throw new Error('useKYCStatus must be used within a KYCProvider');
    }

    return {
        status: context.status,
        isLoading: context.isLoading,
        error: context.error,
        isKYCRequired: context.isKYCRequired,
        refetch: context.refetch
    };
}

/**
 * Hook to manage KYC verification session
 */
export function useKYCSession() {
    const context = useContext(KYCContext);
    if (context === undefined) {
        throw new Error('useKYCSession must be used within a KYCProvider');
    }

    return {
        session: context.session,
        isCreating: context.isCreating,
        error: context.sessionError,
        createSession: context.createSession,
        refreshSession: context.refreshSession,
        clearSession: context.clearSession
    };
}

/**
 * Combined hook for full KYC functionality
 */
export function useKYC() {
    const context = useContext(KYCContext);
    if (context === undefined) {
        throw new Error('useKYC must be used within a KYCProvider');
    }
    return context;
}
